/**
 * Graph Validator
 * Analyzes workflow graph structure for:
 * - Missing Start/Exit nodes
 * - Unreachable nodes (not reachable from Start)
 * - Orphaned paths (paths that never reach an Exit)
 * - Infinite loops (cycles with no exit path)
 */

import { Edge } from '@xyflow/react'
import { WorkflowNode } from '@store/workflowStore'
import { WorkflowNodeType } from '@features/adventures/types/workflow.types'
import {
  ValidationIssue,
  ValidationSeverity,
  ValidationCategory,
  ValidationErrorCodes,
  GraphAnalysisResult,
} from '@features/adventures/types/validation.types'
import { createIssue, EXIT_NODE_TYPES } from './helpers'

interface InternalGraphAnalysisResult extends GraphAnalysisResult {
  issues: ValidationIssue[]
}

/**
 * Build adjacency list from nodes and edges (source -> targets)
 */
function buildAdjacencyList(nodes: WorkflowNode[], edges: Edge[]): Map<string, string[]> {
  const adj = new Map<string, string[]>()
  for (const node of nodes) {
    adj.set(node.id, [])
  }
  for (const edge of edges) {
    const neighbors = adj.get(edge.source) || []
    if (!neighbors.includes(edge.target)) {
      neighbors.push(edge.target)
    }
    adj.set(edge.source, neighbors)
  }
  return adj
}

/**
 * Build reverse adjacency list (target -> sources)
 */
function buildReverseAdjacencyList(nodes: WorkflowNode[], edges: Edge[]): Map<string, string[]> {
  const adj = new Map<string, string[]>()
  for (const node of nodes) {
    adj.set(node.id, [])
  }
  for (const edge of edges) {
    const neighbors = adj.get(edge.target) || []
    if (!neighbors.includes(edge.source)) {
      neighbors.push(edge.source)
    }
    adj.set(edge.target, neighbors)
  }
  return adj
}

/**
 * BFS to find all nodes reachable from a starting node
 */
function bfsReachable(startId: string, adjacency: Map<string, string[]>): Set<string> {
  const visited = new Set<string>()
  const queue = [startId]

  while (queue.length > 0) {
    const current = queue.shift()!
    if (visited.has(current)) continue
    visited.add(current)

    const neighbors = adjacency.get(current) || []
    for (const neighbor of neighbors) {
      if (!visited.has(neighbor)) {
        queue.push(neighbor)
      }
    }
  }

  return visited
}

/**
 * Detect cycles that have no path to an exit node.
 * Uses DFS with three colors: WHITE (unvisited), GRAY (in current path), BLACK (fully processed)
 */
function detectInfiniteCycles(
  nodes: WorkflowNode[],
  adjacency: Map<string, string[]>,
  canReachExit: Set<string>
): string[] {
  const WHITE = 0, GRAY = 1, BLACK = 2
  const color = new Map<string, number>()
  const cycleNodes = new Set<string>()

  // Initialize all nodes as WHITE
  for (const node of nodes) {
    color.set(node.id, WHITE)
  }

  function dfs(nodeId: string, path: string[]): void {
    color.set(nodeId, GRAY)
    path.push(nodeId)

    const neighbors = adjacency.get(nodeId) || []
    for (const neighbor of neighbors) {
      const neighborColor = color.get(neighbor)

      if (neighborColor === GRAY) {
        // Found a cycle - check if any node in the cycle can reach exit
        const cycleStart = path.indexOf(neighbor)
        const cycleMembers = path.slice(cycleStart)

        // If no member of the cycle can reach exit, it's an infinite loop
        const hasExitPath = cycleMembers.some(id => canReachExit.has(id))
        if (!hasExitPath) {
          cycleMembers.forEach(id => cycleNodes.add(id))
        }
      } else if (neighborColor === WHITE) {
        dfs(neighbor, path)
      }
    }

    color.set(nodeId, BLACK)
    path.pop()
  }

  // Start DFS from each unvisited node
  for (const node of nodes) {
    if (color.get(node.id) === WHITE) {
      dfs(node.id, [])
    }
  }

  return Array.from(cycleNodes)
}

/**
 * Analyzes the workflow graph for structural issues
 */
export function analyzeGraph(nodes: WorkflowNode[], edges: Edge[]): InternalGraphAnalysisResult {
  const issues: ValidationIssue[] = []

  // Initialize empty result
  const result: InternalGraphAnalysisResult = {
    issues: [],
    hasUnreachableNodes: false,
    unreachableNodeIds: [],
    hasCycles: false,
    cycleNodeIds: [],
    hasOrphanedPaths: false,
    orphanedPathNodeIds: [],
  }

  // Find Start and Exit nodes
  const startNodes = nodes.filter(n => n.data.nodeType === WorkflowNodeType.Start)
  const exitNodes = nodes.filter(n => EXIT_NODE_TYPES.includes(n.data.nodeType))

  // Validate Start node exists
  if (startNodes.length === 0) {
    issues.push(createIssue({
      nodeId: null,
      severity: ValidationSeverity.Error,
      category: ValidationCategory.GraphStructure,
      code: ValidationErrorCodes.MISSING_START_NODE,
      message: 'Workflow must have a Start node',
    }))
    result.issues = issues
    return result
  }

  if (startNodes.length > 1) {
    issues.push(createIssue({
      nodeId: null,
      severity: ValidationSeverity.Error,
      category: ValidationCategory.GraphStructure,
      code: ValidationErrorCodes.MULTIPLE_START_NODES,
      message: 'Workflow can only have one Start node',
      details: `Found ${startNodes.length} Start nodes`,
    }))
  }

  // Validate at least one Exit node exists
  if (exitNodes.length === 0) {
    issues.push(createIssue({
      nodeId: null,
      severity: ValidationSeverity.Error,
      category: ValidationCategory.GraphStructure,
      code: ValidationErrorCodes.MISSING_EXIT_NODE,
      message: 'Workflow must have at least one Exit node (Success, Failed, or Abandoned)',
    }))
  }

  // Build adjacency lists
  const adjacency = buildAdjacencyList(nodes, edges)
  const reverseAdjacency = buildReverseAdjacencyList(nodes, edges)

  // 1. Find unreachable nodes (BFS from Start)
  const startNodeId = startNodes[0]?.id
  const reachableFromStart = startNodeId ? bfsReachable(startNodeId, adjacency) : new Set<string>()

  const unreachableNodeIds = nodes
    .filter(n =>
      n.data.nodeType !== WorkflowNodeType.Start &&
      !reachableFromStart.has(n.id)
    )
    .map(n => n.id)

  for (const nodeId of unreachableNodeIds) {
    const node = nodes.find(n => n.id === nodeId)!
    issues.push(createIssue({
      nodeId,
      nodeLabel: node.data.label,
      nodeType: node.data.nodeType,
      severity: ValidationSeverity.Warning,
      category: ValidationCategory.GraphStructure,
      code: ValidationErrorCodes.UNREACHABLE_NODE,
      message: 'Node is not reachable from Start',
    }))
  }

  result.hasUnreachableNodes = unreachableNodeIds.length > 0
  result.unreachableNodeIds = unreachableNodeIds

  // 2. Find nodes that can reach Exit (reverse BFS from all Exit nodes)
  const canReachExit = new Set<string>()
  for (const exitNode of exitNodes) {
    const reachable = bfsReachable(exitNode.id, reverseAdjacency)
    reachable.forEach(id => canReachExit.add(id))
  }

  // 3. Find orphaned paths (reachable from Start but can't reach Exit)
  const orphanedPathNodeIds = nodes
    .filter(n =>
      reachableFromStart.has(n.id) &&
      !canReachExit.has(n.id) &&
      !EXIT_NODE_TYPES.includes(n.data.nodeType) &&
      n.data.nodeType !== WorkflowNodeType.Start
    )
    .map(n => n.id)

  for (const nodeId of orphanedPathNodeIds) {
    const node = nodes.find(n => n.id === nodeId)!
    issues.push(createIssue({
      nodeId,
      nodeLabel: node.data.label,
      nodeType: node.data.nodeType,
      severity: ValidationSeverity.Warning,
      category: ValidationCategory.GraphStructure,
      code: ValidationErrorCodes.NO_PATH_TO_EXIT,
      message: 'No path from this node leads to an Exit',
    }))
  }

  result.hasOrphanedPaths = orphanedPathNodeIds.length > 0
  result.orphanedPathNodeIds = orphanedPathNodeIds

  // 4. Detect cycles with no exit path
  const cycleNodeIds = detectInfiniteCycles(nodes, adjacency, canReachExit)

  for (const nodeId of cycleNodeIds) {
    const node = nodes.find(n => n.id === nodeId)!
    issues.push(createIssue({
      nodeId,
      nodeLabel: node.data.label,
      nodeType: node.data.nodeType,
      severity: ValidationSeverity.Error,
      category: ValidationCategory.GraphStructure,
      code: ValidationErrorCodes.INFINITE_LOOP,
      message: 'Node is part of a cycle with no exit path',
    }))
  }

  result.hasCycles = cycleNodeIds.length > 0
  result.cycleNodeIds = cycleNodeIds

  result.issues = issues
  return result
}
